import type { Theme } from "../store/uiStore";

export interface DrawerPalette {
  // wrapper
  bg:                  string;
  border:              string;
  divider:             string;
  // sub-header strip (DrawerHeader)
  headerBg:            string;
  headerBorder:        string;
  cellIdText:          string;     // monospaced cell_id
  cellTitleText:       string;     // optional secondary label (county/tract)
  // section titles + body
  sectionTitle:        string;     // uppercase headings
  subtitle:            string;     // muted small headings inside sections
  bodyText:            string;
  mutedText:           string;     // small label captions
  italicCaveat:        string;     // 12px italic caveat below sections
  // small "panel" cards (EquityImpactPanel container, raw payload pre, etc.)
  panelBg:             string;
  panelBorder:         string;
  // RiskPanel risk-row bar track behind the colored fill
  barTrackBg:          string;
  // info button (i / ?) — same style across panels
  infoBtnBg:           string;
  infoBtnBorder:       string;
  infoBtnText:         string;
  // semantic notices (kept readable in both themes)
  noticeWarnBg:        string;
  noticeWarnText:      string;
  noticeWarnBorder:    string;
  noticeAlertBg:       string;
  noticeAlertText:     string;
  noticeAlertBorder:   string;
  noticeOkBg:          string;
  noticeOkText:        string;
  noticeOkBorder:      string;
  // KV grid value
  kvLabel:             string;
  kvValue:             string;
  // small inline link / accent
  accentLink:          string;
  // floating tooltip (info popovers) — inverts vs the panel underneath so
  // it always reads as a distinct "card on top of the surface".
  tooltipBg:           string;
  tooltipText:         string;
  tooltipMutedText:    string;
  tooltipBorder:       string;
}

const LIGHT: DrawerPalette = {
  bg:                "#ffffff",
  border:            "#e5e7eb",
  divider:           "#f3f4f6",
  headerBg:          "#f9fafb",
  headerBorder:      "#e5e7eb",
  cellIdText:        "#6b7280",
  cellTitleText:     "#111827",
  sectionTitle:      "#374151",
  subtitle:          "#6b7280",
  bodyText:          "#111827",
  mutedText:         "#6b7280",
  italicCaveat:      "#6b7280",
  panelBg:           "#f9fafb",
  panelBorder:       "#e5e7eb",
  barTrackBg:        "#f3f4f6",
  infoBtnBg:         "#f3f4f6",
  infoBtnBorder:     "#d1d5db",
  infoBtnText:       "#6b7280",
  noticeWarnBg:      "#fef3c7",
  noticeWarnText:    "#92400e",
  noticeWarnBorder:  "#fde68a",
  noticeAlertBg:     "#fee2e2",
  noticeAlertText:   "#991b1b",
  noticeAlertBorder: "#fecaca",
  noticeOkBg:        "#d1fae5",
  noticeOkText:      "#065f46",
  noticeOkBorder:    "#6ee7b7",
  kvLabel:           "#9ca3af",
  kvValue:           "#111827",
  accentLink:        "#1d4ed8",
  // Light theme: dark tooltip on light surface.
  tooltipBg:         "#1f2937",
  tooltipText:       "#f9fafb",
  tooltipMutedText:  "#d1d5db",
  tooltipBorder:     "#111827",
};

const DARK: DrawerPalette = {
  bg:                "#111827",
  border:            "#374151",
  divider:           "#1f2937",
  headerBg:          "#1f2937",
  headerBorder:      "#374151",
  cellIdText:        "#9ca3af",
  cellTitleText:     "#f9fafb",
  sectionTitle:      "#e5e7eb",
  subtitle:          "#9ca3af",
  bodyText:          "#f9fafb",
  mutedText:         "#9ca3af",
  italicCaveat:      "#9ca3af",
  panelBg:           "#1f2937",
  panelBorder:       "#374151",
  barTrackBg:        "#374151",
  infoBtnBg:         "#374151",
  infoBtnBorder:     "#4b5563",
  infoBtnText:       "#d1d5db",
  // Slightly darker washed-out tints so the notice keeps semantic colour
  // without blowing out the dark drawer.
  noticeWarnBg:      "#3f2d10",
  noticeWarnText:    "#fcd34d",
  noticeWarnBorder:  "#78350f",
  noticeAlertBg:     "#3b1212",
  noticeAlertText:   "#fca5a5",
  noticeAlertBorder: "#7f1d1d",
  noticeOkBg:        "#0e2c20",
  noticeOkText:      "#6ee7b7",
  noticeOkBorder:    "#065f46",
  kvLabel:           "#9ca3af",
  kvValue:           "#f9fafb",
  accentLink:        "#93c5fd",
  // Dark theme: light tooltip on dark surface — inverts the relationship.
  tooltipBg:         "#f9fafb",
  tooltipText:       "#111827",
  tooltipMutedText:  "#4b5563",
  tooltipBorder:     "#d1d5db",
};

export function drawerPalette(theme: Theme): DrawerPalette {
  return theme === "light" ? LIGHT : DARK;
}
